import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1556079337-a837a2d11f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxib3N0b24lMjBza3lsaW5lJTIwY2l0eSUyMGFlcmlhbHxlbnwwfHx8fDE3OTA3ODg0MTh8MA&ixlib=rb-4.1.0&q=85";

const ease = [0.16, 1, 0.3, 1];

const LINES = [
  { text: "The bridge between", cls: "text-charcoal-900" },
  { text: "entertainment", cls: "text-charcoal-900" },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);

  return (
    <section id="pulse" ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-white pt-28 sm:pt-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 sm:px-8 lg:min-h-[calc(100vh-7rem)] lg:grid-cols-12 lg:gap-10 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease }}
            className="inline-flex items-center gap-3 border border-line bg-surface px-4 py-2"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-steel sm:text-[11px]">
              True Life Entertainment · Boston, MA
            </span>
          </motion.div>

          <h1 className="mt-8 font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-0.5">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease }}
                  data-testid={`hero-line-${i}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block text-accent"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.46, ease }}
                data-testid="hero-line-2"
              >
                &amp; technology.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease }}
            className="mt-7 max-w-xl text-base leading-relaxed text-steel sm:text-lg"
          >
            True Life Entertainment is a technology company and an entertainment company. Our core
            products, LANTIX and LANTIX PRO, power social ticketing and event labor, while we
            prepare the next form of the entertainment market and introduce the new form of
            robotics to the industry.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.84, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#ventures"
              data-testid="hero-primary-cta"
              onClick={(e) => {
                e.preventDefault();
                window.__lenis
                  ? window.__lenis.scrollTo("#ventures", { offset: -80, duration: 1.2 })
                  : document.querySelector("#ventures").scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-2 rounded-md bg-accent px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-accent-dark"
            >
              Explore Our Ventures
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/contact"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center rounded-md border border-line px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-charcoal-900 transition-colors duration-200 hover:border-charcoal-900"
            >
              Contact Us
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-line pt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-steel"
          >
            <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-accent" /> Boston, Massachusetts</span>
            <span>Beta Launching 2026</span>
            <span>Expanding to Trinidad &amp; Tobago</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.4, ease }}
          className="relative lg:col-span-5"
        >
          <div className="treated-hover relative overflow-hidden">
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="Aerial view of Boston"
              className="treated aspect-[4/5] w-full object-cover"
              loading="eager"
            />
          </div>
          <div className="pointer-events-none absolute -bottom-4 -left-4 -z-10 hidden h-full w-full border border-accent/40 sm:block" />
          <div className="absolute bottom-6 left-6 bg-charcoal-900 p-5 sm:p-6">
            <div className="font-mono text-2xl font-semibold tracking-tight text-white sm:text-3xl">$6.2B</div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Boston live events market, annually
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
